import { Icon } from '@iconify/react/dist/iconify.js';
import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { PaginationLimit } from '../../../../components/PaginationLimit';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import { SOCIETY_CONTROLLER, SOCIETY_MANAGEMENT_CREATE, SOCIETY_MANAGEMENT_EDIT, SOCIETY_MANAGEMENT_COMMUNICATION_SETTINGS } from '../../../../Routing/routes';
import { getComparator, stableSort } from '../../../../utils/helpers/universalFunctions';
import Loader from '../../../../components/Looder';
import SocietyDetailsModal from './SocietyDetailsModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import AuditLogModal from './AuditLogModal';
import { deleteCommunity, editCommunity, getCommunityDetails } from '../../../../redux/actions/SocietyController/CtrlCommunityManagementAction/CtrlCommunityManagement';
import ToggleSwitch from '../../../../components/ToggleSwitch';
import { getBrandsList } from '../../../../redux/actions/SocietyController/CtrlBrandAction/CtrlBrand';
import { formatIstDate } from '../../../../utils/helpers/istDate';

const TYPE_OPTIONS = [
    { value: 'all', label: 'All Types' },
    { value: 'RESIDENTIAL', label: 'Residential' },
    { value: 'COMMERCIAL', label: 'Commercial' },
];

const STATUS_OPTIONS = [
    { value: 'all', label: 'All Status' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' },
    { value: 'SUSPENDED', label: 'Suspended' },
    { value: 'UNDER_REVIEW', label: 'Under Review' },
];

// Badge styling per society status (PATCH /api/societies/:id accepts these four).
const STATUS_BADGE = {
    ACTIVE: { label: 'Active', cls: 'tw-bg-emerald-50 tw-text-emerald-600 tw-border-emerald-100', dot: 'tw-bg-emerald-500' },
    INACTIVE: { label: 'Inactive', cls: 'tw-bg-gray-50 tw-text-gray-500 tw-border-gray-100', dot: 'tw-bg-gray-400' },
    UNDER_REVIEW: { label: 'Under Review', cls: 'tw-bg-amber-50 tw-text-amber-700 tw-border-amber-100', dot: 'tw-bg-amber-500' },
    SUSPENDED: { label: 'Suspended', cls: 'tw-bg-red-50 tw-text-red-600 tw-border-red-100', dot: 'tw-bg-red-500' },
};

const SocietyListLayer = ({
    data = [],
    getTableData = () => { },
    loading = true,
    columns = [],
    page = 1,
    total = 0,
    limit = 10,
    searchQuery,
    setSearchQuery = () => { },
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedSociety, setSelectedSociety] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [societyToDelete, setSocietyToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [auditLogModalOpen, setAuditLogModalOpen] = useState(false);
    const [auditLogSociety, setAuditLogSociety] = useState(null);
    const [itemToDelete, setItemToDelete] = useState(null);

    const [search, setSearch] = useState('');
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState(null);

    const handleSort = (field) => {
        if (!field) return;
        const isAsc = orderBy === field && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(field);
    };

    const handleSearchSubmit = () => {
        setSearchQuery((prev) => ({ ...prev, page: 1, search }));
    };

    const handleClear = () => {
        setSearch('');
        setSearchQuery({ page: 1, limit: 10, search: '', status: 'all', type: 'all' });
    };

    const handlePageChange = (currentPage) => {
        if (searchQuery.page !== currentPage) {
            setSearchQuery({ ...searchQuery, page: currentPage });
        }
    };

    const handleLimitChange = (e) => {
        setSearchQuery({ ...searchQuery, page: 1, limit: e.target.value });
    };

    const handleConfirmDelete = () => {
        setIsDeleting(true);
        dispatch(deleteCommunity({
            id: itemToDelete?.id,
            onSuccess: () => {
                getTableData();
                setDeleteModalOpen(false);
                setSocietyToDelete(null);
                setIsDeleting(false);
            },
        }));
    };

    // Rows with a status PATCH in flight — their toggle is disabled until it settles.
    const [statusPendingIds, setStatusPendingIds] = useState([]);

    // Toggle on → ACTIVE (activate / reactivate, also clears UNDER_REVIEW);
    // toggle off → INACTIVE. Suspended rows never render the toggle.
    const handleStatusToggle = (item, on) => {
        const nextStatus = on ? 'ACTIVE' : 'INACTIVE';
        const settle = () => setStatusPendingIds((ids) => ids.filter((x) => x !== item.id));
        setStatusPendingIds((ids) => [...ids, item.id]);
        dispatch(editCommunity({
            id: item.id,
            status: nextStatus,
            successMessage: `${item.title} ${on ? 'activated' : 'deactivated'}`,
            onSuccess: () => { settle(); getTableData(); },
            onError: settle,
        }));
    };

    const sortedData = useMemo(
        () => (orderBy ? stableSort(data, getComparator(order, orderBy)) : data),
        [data, order, orderBy]
    );

    return (
        <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
            {/* Filters Bar */}
            <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <div className="tw-relative">
                        <Icon icon="ion:search-outline" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                        <input
                            type="text"
                            placeholder="Search by name..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
                            className="tw-h-8 tw-pl-8 tw-pr-3 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500 focus:tw-ring-1 focus:tw-ring-rose-500 tw-w-56"
                        />
                    </div>
                    <div className="tw-w-40">
                        <SearchableDropdown
                            value={searchQuery?.type || 'all'}
                            onChange={(v) => setSearchQuery((prev) => ({ ...prev, page: 1, type: v }))}
                            options={TYPE_OPTIONS}
                            placeholder="Type"
                            clearable={false}
                            small
                        />
                    </div>
                    <div className="tw-w-40">
                        <SearchableDropdown
                            value={searchQuery?.status || 'all'}
                            onChange={(v) => setSearchQuery((prev) => ({ ...prev, page: 1, status: v }))}
                            options={STATUS_OPTIONS}
                            placeholder="Status"
                            clearable={false}
                            small
                        />
                    </div>
                    <button
                        onClick={handleClear}
                        className="tw-h-8 tw-inline-flex tw-items-center tw-px-3 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50"
                    >
                        Clear
                    </button>
                </div>
                <Link
                    to={SOCIETY_CONTROLLER + SOCIETY_MANAGEMENT_CREATE}
                    className="tw-h-8 tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-transition-colors"
                >
                    <Icon icon="mdi:plus" className="tw-text-sm" />
                    Create Community
                </Link>
            </div>

            {/* Table */}
            <div className="tw-overflow-x-auto">
                <table className="tw-w-full tw-text-xs">
                    <thead>
                        <tr className="tw-bg-gradient-to-r tw-from-slate-50 tw-to-gray-50">
                            {columns.map((col) => {
                                const sortable = col.sort !== false;
                                const isActive = orderBy === col.id;
                                return (
                                    <th
                                        key={col.id}
                                        onClick={() => sortable && handleSort(col.id)}
                                        style={{ minWidth: col.width || 'auto' }}
                                        className={`tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px] ${sortable ? 'tw-cursor-pointer hover:tw-text-rose-600' : ''}`}
                                    >
                                        <span className="tw-inline-flex tw-items-center tw-gap-1">
                                            {col.label}
                                            {sortable && (
                                                <Icon
                                                    icon={isActive
                                                        ? (order === 'asc' ? 'mdi:menu-up' : 'mdi:menu-down')
                                                        : 'mdi:unfold-more-horizontal'}
                                                    className={isActive ? 'tw-text-rose-500' : 'tw-text-gray-300'}
                                                />
                                            )}
                                        </span>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody className="tw-divide-y tw-divide-gray-50">
                        {loading ? (
                            <tr>
                                <td colSpan={columns.length} className="tw-text-center tw-py-12">
                                    <Loader />
                                </td>
                            </tr>
                        ) : sortedData?.length > 0 ? (
                            sortedData.map((item) => (
                                <tr key={item.id} className="hover:tw-bg-slate-50/50 tw-transition-colors">
                                    <td className="tw-px-4 tw-py-2.5 tw-text-gray-400">{item?.slNo}</td>
                                    <td className="tw-px-4 tw-py-2.5 tw-text-rose-600 tw-font-medium tw-font-mono">#{item.id}</td>
                                    <td className="tw-px-4 tw-py-2.5 tw-text-gray-600">
                                        <span className="tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-indigo-50 tw-text-indigo-600 tw-text-[10px] tw-font-medium">
                                            {item.type || '—'}
                                        </span>
                                    </td>
                                    <td className="tw-px-4 tw-py-2.5">
                                        <div className="tw-flex tw-items-center tw-gap-2">
                                            <Icon icon="mdi:earth" className="tw-text-rose-400 tw-text-base" />
                                            <span className="tw-font-medium tw-text-gray-800">{item.title}</span>
                                        </div>
                                    </td>
                                    <td className="tw-px-4 tw-py-2.5 tw-text-gray-500 tw-whitespace-nowrap">
                                        {item.createdAt ? formatIstDate(item.createdAt) : '—'}
                                    </td>
                                    <td className="tw-px-4 tw-py-2.5 tw-whitespace-nowrap">
                                        <div className="tw-flex tw-items-center tw-gap-3 tw-text-gray-700">
                                            <span className="tw-inline-flex tw-items-center tw-gap-1" title="Active flats">
                                                <Icon icon="mdi:home-outline" className="tw-text-gray-400 tw-text-sm" />
                                                <span className="tw-font-semibold">{item.flats ?? 0}</span>
                                                <span className="tw-text-[11px] tw-text-gray-500">{item.flats === 1 ? 'flat' : 'flats'}</span>
                                            </span>
                                            <span className="tw-inline-flex tw-items-center tw-gap-1" title="Total members">
                                                <Icon icon="mdi:account-group-outline" className="tw-text-gray-400 tw-text-sm" />
                                                <span className="tw-font-semibold">{item.members ?? 0}</span>
                                                <span className="tw-text-[11px] tw-text-gray-500">{item.members === 1 ? 'member' : 'members'}</span>
                                            </span>
                                        </div>
                                        {/* Owners / tenants split — only worth a line when there are members */}
                                        {item.members > 0 && (
                                            <div className="tw-text-[10px] tw-text-gray-400 tw-mt-0.5">
                                                {item.owners} {item.owners === 1 ? 'owner' : 'owners'} · {item.tenants} {item.tenants === 1 ? 'tenant' : 'tenants'}
                                            </div>
                                        )}
                                    </td>
                                    <td className="tw-px-4 tw-py-2.5">
                                        {(() => {
                                            const badge = STATUS_BADGE[item.status] || STATUS_BADGE.INACTIVE;
                                            return (
                                                <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-border tw-whitespace-nowrap ${badge.cls}`}>
                                                    <span className={`tw-w-1.5 tw-h-1.5 tw-rounded-full ${badge.dot}`}></span>
                                                    {STATUS_BADGE[item.status] ? badge.label : (item.status || 'Inactive')}
                                                </span>
                                            );
                                        })()}
                                    </td>
                                    <td className="tw-px-4 tw-py-2.5">
                                        {/* A suspended society is read-only here — no toggle, view, edit or delete */}
                                        {item.status === 'SUSPENDED' ? (
                                            <span className="tw-text-gray-300">—</span>
                                        ) : (
                                        <div className="tw-flex tw-items-center tw-gap-1">
                                            {/* Active/Inactive switch */}
                                            <span
                                                className="tw-inline-flex tw-items-center tw-mr-1"
                                                title={statusPendingIds.includes(item.id)
                                                    ? 'Updating…'
                                                    : item.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                                            >
                                                <ToggleSwitch
                                                    size="sm"
                                                    id={`society-status-${item.id}`}
                                                    checked={item.status === 'ACTIVE'}
                                                    disabled={statusPendingIds.includes(item.id)}
                                                    onChange={(on) => handleStatusToggle(item, on)}
                                                />
                                            </span>
                                            <button
                                                onClick={() => {
                                                    setSelectedSociety({
                                                        id: item?.id,
                                                        name: item?.title,
                                                        type: item?.type,
                                                        status: item?.status,
                                                        createdOn: item?.createdAt,
                                                    });
                                                    dispatch(getCommunityDetails({ id: item?.id }));
                                                    dispatch(getBrandsList({ page: 1, limit: 100 }));
                                                    setModalOpen(true);
                                                }}
                                                className="tw-p-1 tw-rounded tw-bg-slate-50 tw-text-slate-600 hover:tw-bg-slate-100 tw-transition-colors"
                                                title="View"
                                            >
                                                <Icon icon="iconamoon:eye-light" className="tw-text-sm" />
                                            </button>
                                            <button
                                                onClick={() => navigate(SOCIETY_CONTROLLER + SOCIETY_MANAGEMENT_EDIT.replace(':id', item?.id))}
                                                className="tw-p-1 tw-rounded tw-bg-blue-50 tw-text-blue-600 hover:tw-bg-blue-100 tw-transition-colors"
                                                title="Edit"
                                            >
                                                <Icon icon="lucide:edit" className="tw-text-sm" />
                                            </button>
                                            <button
                                                onClick={() => navigate(
                                                    SOCIETY_CONTROLLER + SOCIETY_MANAGEMENT_COMMUNICATION_SETTINGS.replace(':id', item?.id),
                                                    { state: { societyName: item?.title } },
                                                )}
                                                className="tw-p-1 tw-rounded tw-bg-amber-50 tw-text-amber-600 hover:tw-bg-amber-100 tw-transition-colors"
                                                title="Settings"
                                            >
                                                <Icon icon="solar:settings-linear" className="tw-text-sm" />
                                            </button>
                                            <button
                                                onClick={() => { setItemToDelete({ id: item?.id }); setSocietyToDelete(item); setDeleteModalOpen(true); }}
                                                className="tw-p-1 tw-rounded tw-bg-red-50 tw-text-red-500 hover:tw-bg-red-100 tw-transition-colors"
                                                title="Delete"
                                            >
                                                <Icon icon="mingcute:delete-2-line" className="tw-text-sm" />
                                            </button>
                                        </div>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className="tw-text-center tw-py-12">
                                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                        <Icon icon="mdi:database-off-outline" className="tw-text-3xl tw-text-gray-300" />
                                        <p className="tw-text-xs tw-text-gray-400">No communities found</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {data?.length > 0 && (
                <div className="tw-border-t tw-border-gray-100 tw-px-3 tw-py-2">
                    <PaginationLimit
                        currentPage={page}
                        totalRecords={total}
                        limit={limit || 10}
                        handleLimitChange={handleLimitChange}
                        handlePageChange={handlePageChange}
                    />
                </div>
            )}

            {/* Modals */}
            <SocietyDetailsModal
                open={modalOpen}
                handleClose={() => { setModalOpen(false); setSelectedSociety(null); }}
                society={selectedSociety}
            />

            {deleteModalOpen && (
                <DeleteConfirmModal
                    open={deleteModalOpen}
                    handleClose={() => { setDeleteModalOpen(false); setSocietyToDelete(null); }}
                    handleConfirm={handleConfirmDelete}
                    title="Delete Community"
                    itemName={societyToDelete?.title}
                    itemId={societyToDelete?.id}
                    isLoading={isDeleting}
                />
            )}

            <AuditLogModal
                open={auditLogModalOpen}
                handleClose={() => { setAuditLogModalOpen(false); setAuditLogSociety(null); }}
                society={auditLogSociety}
            />
        </div>
    );
};

export default SocietyListLayer;
